import React, { useState, useEffect, useMemo, useRef } from 'react'
import to from 'await-catch'
import 'react-datepicker/dist/react-datepicker.css'
import { Formik } from 'formik'
import MapContainer from '../../../components/map/MapContainer'
import { connect } from 'react-redux'
import {
  addFlashMessage,
  hideLoader,
  showLoader,
  setAppointmentsBookingSearchFetched,
  setAppointmentsBookingForm,
  setAppointmentsBookingSubmittedFilters,
  setAppointmentsBookingSearchResults,
  setAppointmentsBookingSelectedAppointments,
  setAppointmentsBookingSelectedSpecialtyTab
} from '../../../actions'
import {
	Container,
	Header,
	ContentWrapper,
	Sidebar,
	FilterToggle,
	Toggle,
	ToggleInput,
	ToggleSlider,
	ResultsList,
	MapWrapper,
	ModalWrapper,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalTitle,
	CloseButton,
	ModalFilterGrid,
	ModalFooter
} from '../../../components/ui/searchMap/styled'
import AppointmentCart from '../../../components/ui/searchMap/AppointmentCart'
import {
  groupTurnosByProfessionalClinic,
  filterMedicsByNext7Days
} from '../../../utils/groupTurnosByProfessional'
import { useAvailableAppointments } from '../../../hooks/useAvailableAppointments'
import { useAppointmentFilterHandlers } from '../../../hooks/useAppointmentFilterHandlers'
import { buildSearchFiltersText, countSearchFilters, toDateOnly } from '../../../utils/formatDate'

import { parseProfessionalName } from '../../../utils/stringsUtils'
import { mapToOptions } from '../../../utils/mapToOptions'
import { getProfessionals as getProfessionalsApi } from '../../../api'
import { Button } from '../../../components/ui/button'
import useIsMobile from '../../../hooks/useIsMobile'
import { BOOKING_TYPES } from '../constants'
import { validateDateAndTimeRanges } from '../schemas/searchMapSchemas'
import ModalFilter from '../../../components/modal/ModalFilter'
import BackPageHeader from '../../../components/ui/header/BackPageHeader'
import colors from '../../../styles/colors'
import SearchSectionEditor from '../../../components/ui/searchMap/SearchSectionEditor'
import AppointmentFilterFields from '../../../components/ui/searchMap/AppointmentFilterFields'
import EmptyState from '../../../components/emptyState'
import Tabs from '../../../components/tabs'
import { Text2, Title } from '../../../components/ui/text'
import MobileFilterBar from '../../../components/ui/searchMap/MobileFilterBar'
import DoctorCard from '../../../components/ui/searchMap/DoctorCard'
import MobileViewToggle from '../../../components/ui/searchMap/MobileViewToggle'
import ClinicAppointmentsModal from '../../../components/ui/searchMap/ClinicAppointmentsModal'

const normalizeToArray = value => (Array.isArray(value) ? value : value ? [value] : [])

const createMultipleFiltersState = ({ source, fixedClinics, fixedClinicCodes }) => ({
	speciality: source?.speciality || '',
	specialities: Array.isArray(source?.specialities)
		? source.specialities
		: source?.speciality
		? [source.speciality]
		: [],
	clinic: fixedClinics,
	clinicCode: fixedClinicCodes,
	professional: source?.professional || null,
	dateFrom: source?.dateFrom || null,
	dateTo: source?.dateTo || null,
	hourFrom: source?.hourFrom || null,
	hourTo: source?.hourTo || null
})

const toMinutes = date => {
	if (!date) return null
	const parsed = new Date(date)
	if (Number.isNaN(parsed.getTime())) return null
	return parsed.getHours() * 60 + parsed.getMinutes()
}

const matchDateAndTimeRange = (turnDateTime, filters) => {
	if (!turnDateTime) return false

	const turnDate = new Date(turnDateTime)
	if (Number.isNaN(turnDate.getTime())) return false

	const turnDay = toDateOnly(turnDate).getTime()
	if (filters.dateFrom && turnDay < toDateOnly(filters.dateFrom).getTime()) return false
	if (filters.dateTo && turnDay > toDateOnly(filters.dateTo).getTime()) return false

	const turnMinutes = toMinutes(turnDate)
	const fromMinutes = toMinutes(filters.hourFrom)
	const toMinutesValue = toMinutes(filters.hourTo)

	if (fromMinutes != null && turnMinutes < fromMinutes) return false
	if (toMinutesValue != null && turnMinutes > toMinutesValue) return false

	return true
}

const SearchMap = ({
	onNext,
	bookingType,
	formData,
	submittedFilters,
	clinics,
	specialties,
	professionals,
	professionalsKey,
	searchFetched,
	selectedSpecialtyTabStored,
	onBack,
	patient,
	showLoader,
	hideLoader,
	searchResults,
	cartTurns,
	setAppointmentsBookingForm,
	setAppointmentsBookingSubmittedFilters,
	setAppointmentsBookingSearchResults,
	setAppointmentsBookingSearchFetched,
	setAppointmentsBookingSelectedAppointments,
	setAppointmentsBookingSelectedSpecialtyTab,
	getProfessionals,
	addFlashMessage
}) => {
	const isMultTurns = bookingType === BOOKING_TYPES.MULTIPLE
	const showAppointmentCard = isMultTurns && Array.isArray(cartTurns) && cartTurns.length > 0
	const fixedClinics = useMemo(() => normalizeToArray(submittedFilters?.clinic), [submittedFilters])
	const fixedClinicCodes = useMemo(
		() => normalizeToArray(submittedFilters?.clinicCode).map(code => String(code)),
		[submittedFilters]
	)
	const hasStoredResults = Array.isArray(searchResults) && searchResults.length > 0
	const [multipleFilters, setMultipleFilters] = useState(() =>
		createMultipleFiltersState({ source: formData, fixedClinics, fixedClinicCodes })
	)
	const [showNext7Days, setShowNext7Days] = useState(false)
	const [mapCenter, setMapCenter] = useState({ lat: -34.5829, lng: -58.4421 })
	const [zoom, setZoom] = useState(13)
	const [selectedMedicId, setSelectedMedicId] = useState(null)
	const [isEditingSearch, setIsEditingSearch] = useState(false)
	const [showFiltersModal, setShowFiltersModal] = useState(false)
	const [showMap, setShowMap] = useState(false)
	const [selectedClinicModal, setSelectedClinicModal] = useState(null)
	const [searchTrigger, setSearchTrigger] = useState(
		searchFetched || hasStoredResults ? null : 0
	)
	const [selectedSpecialtyTab, setSelectedSpecialtyTab] = useState(
		selectedSpecialtyTabStored || null
	)
	const isMobile = useIsMobile()

	const {
		loadedProfessionalsKey,
		isLoadingProfessionals,
		onChangeSpeciality,
		onChangeClinic,
		getProfessionalsKey,
		getClinicOptions
	} = useAppointmentFilterHandlers({
		clinics,
		professionals,
		getProfessionals,
		initialLoadedKey: professionalsKey,
		onSpecialityChange: payload => setAppointmentsBookingForm(payload),
		onClinicChange: payload => setAppointmentsBookingForm(payload)
	})

	const { appointments, hasFetched } = useAvailableAppointments({
		patient,
		filters: submittedFilters,
		trigger: searchTrigger,
		enabled: searchTrigger !== null,
		existingResults: searchResults,
		showLoader,
		hideLoader,
		addFlashMessage
	})

	useEffect(() => {
		if (!hasFetched) return
		setAppointmentsBookingSearchResults(appointments)
		setAppointmentsBookingSearchFetched(true)
	}, [
		hasFetched,
		appointments,
		setAppointmentsBookingSearchResults,
		setAppointmentsBookingSearchFetched
	])

	// Muestra los turnos recién traídos por el hook; si no hubo fetch, cae al store.
	const effectiveResults = hasFetched ? appointments : searchResults

	useEffect(() => {
		if (!isMultTurns) return

		setMultipleFilters(prev =>
			createMultipleFiltersState({
				source: {
					...prev,
					...formData,
					clinic: fixedClinics,
					clinicCode: fixedClinicCodes
				},
				fixedClinics,
				fixedClinicCodes
			})
		)
	}, [isMultTurns, formData, fixedClinics, fixedClinicCodes])

	// Carga los profesionales de todas las especialidades una sola vez al entrar; los tabs leen del cache.
	const [professionalsBySpecialty, setProfessionalsBySpecialty] = useState({})
	const loadedProfessionalsRef = useRef(new Set())

	const selectedSpecialityIds = useMemo(
		() => (Array.isArray(multipleFilters.specialities) ? multipleFilters.specialities : []),
		[multipleFilters.specialities]
	)

	useEffect(() => {
		if (!isMultTurns || selectedSpecialityIds.length === 0) return
		const idObraSocial = patient?.idObraSocial || patient?.obraSocial?.id
		const idPlan = patient?.idPlan || patient?.plan?.id
		if (!idObraSocial || !idPlan) return

		const clinicas = [...fixedClinicCodes].sort().join(',')

		selectedSpecialityIds.forEach(async specialityId => {
			const cacheKey = `${specialityId}-${clinicas}`
			if (loadedProfessionalsRef.current.has(cacheKey)) return
			loadedProfessionalsRef.current.add(cacheKey)

			const request = {
				idEspecialidad: specialityId,
				idObraSocial,
				idPlan,
				esVideoconsulta: false,
				...(clinicas ? { clinicas } : {})
			}
			const [err, response] = await to(getProfessionalsApi(request))
			if (err) {
				loadedProfessionalsRef.current.delete(cacheKey)
				return
			}
			const options = mapToOptions(
				response.data || response,
				'idProfesional',
				'nombreProfesional'
			).map(option => ({ ...option, label: parseProfessionalName(option.label) }))
			setProfessionalsBySpecialty(prev => ({ ...prev, [cacheKey]: options }))
		})
		// Se cargan una sola vez por especialidad/clínica (guard por cacheKey), no en cada cambio de tab.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMultTurns, selectedSpecialityIds, fixedClinicCodes])

	const multipleProfessionalOptions = useMemo(() => {
		if (!isMultTurns || selectedSpecialtyTab == null) return []
		const clinicas = [...fixedClinicCodes].sort().join(',')
		return professionalsBySpecialty[`${selectedSpecialtyTab}-${clinicas}`] || []
	}, [isMultTurns, selectedSpecialtyTab, professionalsBySpecialty, fixedClinicCodes])

	const filteredSearchResults = useMemo(() => {
		if (!isMultTurns) return effectiveResults

		const fixedClinicTokens = new Set([...fixedClinicCodes, ...fixedClinics].map(String))
		const shouldApplyClinicFilter =
			fixedClinicTokens.size > 0 &&
			effectiveResults.some(turno => fixedClinicTokens.has(String(turno.clinica ?? '')))

		return effectiveResults.filter(turno => {
			if (shouldApplyClinicFilter && !fixedClinicTokens.has(String(turno.clinica ?? ''))) {
				return false
			}

			if (
				multipleFilters.professional &&
				String(turno.idProfesional ?? '') !== String(multipleFilters.professional)
			) {
				return false
			}

			return matchDateAndTimeRange(turno.fechaTurnoDateTime, multipleFilters)
		})
	}, [isMultTurns, effectiveResults, fixedClinicCodes, fixedClinics, multipleFilters])

	const groupedMedics = useMemo(
		() => groupTurnosByProfessionalClinic(filteredSearchResults),
		[filteredSearchResults]
	)

	const specialtyName =
		specialties.find(s => s.value === submittedFilters.speciality)?.label || ''

	const handleMapPositionChange = (newZoom, newCenter) => {
		setZoom(newZoom)
		setMapCenter(newCenter)
	}

	const handleOpenFilters = () => {
		if (isMultTurns) {
			setShowFiltersModal(true)
			return
		}

		if (isMobile) {
			setShowFiltersModal(true)
		} else {
			setIsEditingSearch(true)
		}
	}

	const handleSelectMarker = clinicData => {
		// clinicData ahora es un objeto con { clinica, latitud, longitud, turnos }
		if (clinicData?.clinica) {
			setSelectedMedicId(clinicData.clinica)
			// Abre el modal en mobile y desktop
			if (clinicData.turnos && clinicData.turnos.length > 0) {
				setSelectedClinicModal(clinicData.turnos[0])
			}
		}
	}

	const handleSelectCard = medicId => {
		setSelectedMedicId(medicId)
		const medic = groupedMedics.find(m => m.idTurno === medicId)
		if (medic) {
			setMapCenter({ lat: medic.latitud, lng: medic.longitud })
			setZoom(15)
		}
	}

	const handleBookAppointment = (medicId, clinic) => {
		const filteredAppointments = effectiveResults.filter(
			t => t.idProfesional === medicId && t.clinica === clinic
		)
		setAppointmentsBookingSelectedAppointments(filteredAppointments)
		onNext()
	}

	const handleCartContinue = () => {
		onNext({step: 5})
	}

	const handleSearchSubmit = (values, closeEditor = () => {}) => {
		setAppointmentsBookingForm(values)
		setAppointmentsBookingSubmittedFilters(values)
		setAppointmentsBookingSearchFetched(false)
		setSearchTrigger(prev => (prev == null ? 1 : prev + 1))
		closeEditor()
	}

	const handleMultipleFilterSubmit = (values, closeEditor = () => {}) => {
		const nextFilters = createMultipleFiltersState({
				source: {
					...multipleFilters,
					...values
				},
			fixedClinics,
			fixedClinicCodes
		})

		setMultipleFilters(nextFilters)
		setAppointmentsBookingForm(nextFilters)
		setAppointmentsBookingSubmittedFilters(nextFilters)
		setAppointmentsBookingSearchFetched(false)
		setSearchTrigger(prev => (prev == null ? 1 : prev + 1))
		closeEditor()
	}

	const handleMultipleModalFilterSubmit = (values, closeEditor = () => {}) => {
		const nextFilters = createMultipleFiltersState({
			source: {
				...multipleFilters,
				...values
			},
			fixedClinics,
			fixedClinicCodes
		})

		setMultipleFilters(nextFilters)
		setAppointmentsBookingForm(nextFilters)

		const scopedSpecialty = selectedSpecialtyTab
		const scopedFilters =
			scopedSpecialty != null
				? { ...nextFilters, specialities: [scopedSpecialty], mergeIntoExisting: true }
				: nextFilters

		setAppointmentsBookingSubmittedFilters(scopedFilters)
		setAppointmentsBookingSearchFetched(false)
		setSearchTrigger(prev => (prev == null ? 1 : prev + 1))
		closeEditor()
	}

	const specialtyTabs = useMemo(() => {
		if (isMultTurns) {
			const selectedSpecialityIds = multipleFilters.specialities || []
			const byId = new Map()
			
			selectedSpecialityIds.forEach(id => {
				const specialty = specialties.find(s => s.value === id)
				if (specialty) {
					byId.set(id, { id, name: specialty.label })
				}
			})
			
			return Array.from(byId.values())
		}
		
		const byId = new Map()
		groupedMedics.forEach(m => {
			if (m.idEspecialidad != null && !byId.has(m.idEspecialidad)) {
				byId.set(m.idEspecialidad, { id: m.idEspecialidad, name: m.nombreEspecialidad })
			}
		})
		return Array.from(byId.values())
	}, [groupedMedics, isMultTurns, multipleFilters.specialities, specialties])

	useEffect(() => {
		if (!isMultTurns || specialtyTabs.length === 0) return
		setSelectedSpecialtyTab(prev =>
			prev != null && specialtyTabs.some(t => t.id === prev) ? prev : specialtyTabs[0].id
		)
	}, [specialtyTabs, isMultTurns])

	useEffect(() => {
		if (isMultTurns) return
		if (selectedSpecialtyTabStored == null) return
		setAppointmentsBookingSelectedSpecialtyTab(null)
	}, [
		isMultTurns,
		selectedSpecialtyTabStored,
		setAppointmentsBookingSelectedSpecialtyTab
	])

	useEffect(() => {
		if (!isMultTurns) return

		setMultipleFilters(prev =>
			prev?.professional
				? {
					...prev,
					professional: null
				}
				: prev
		)

		setAppointmentsBookingSelectedSpecialtyTab(selectedSpecialtyTab)
	}, [isMultTurns, selectedSpecialtyTab, setAppointmentsBookingSelectedSpecialtyTab])

	const displayedMedics = useMemo(() => {
		let medics = groupedMedics
		if (isMultTurns && selectedSpecialtyTab != null) {
			medics = medics.filter(m => m.idEspecialidad === selectedSpecialtyTab)
		}
		if (showNext7Days) medics = filterMedicsByNext7Days(medics)
		return medics
	}, [groupedMedics, showNext7Days, selectedSpecialtyTab, isMultTurns])

	const selectedFiltersText = buildSearchFiltersText({
		formData: isMultTurns ? multipleFilters : submittedFilters,
		clinics,
		professionals
	})

	const multipleFiltersCount = useMemo(() => {
		let count = 0

		if (multipleFilters.professional) count++
		if (multipleFilters.dateFrom || multipleFilters.dateTo) count++
		if (multipleFilters.hourFrom && multipleFilters.hourTo) count++

		return count
	}, [multipleFilters])

	const filterFieldsProps = {
		specialties,
		clinics,
		professionals,
		loadedProfessionalsKey,
		isLoadingProfessionals,
		onChangeSpeciality,
		onChangeClinic,
		getProfessionalsKey,
		getClinicOptions
	}

	const multipleFilterFieldsProps = {
		clinics,
		professionals: [],
		professionalOptionsOverride: multipleProfessionalOptions,
		clinicDisabled: true,
		disableProfessionalFetch: true,
		professionalDisabled: false,
		onChangeClinic: () => {},
		getProfessionalsKey: () => null,
		getClinicOptions: () => false,
		loadedProfessionalsKey: null,
		isLoadingProfessionals: false
	}

	const baseMultipleTurnsForFilters = useMemo(() => {
		if (!isMultTurns) return []

		const fixedClinicTokens = new Set([...fixedClinicCodes, ...fixedClinics].map(String))
		const shouldApplyClinicFilter =
			fixedClinicTokens.size > 0 &&
			searchResults.some(turno => fixedClinicTokens.has(String(turno.clinica ?? '')))

		return searchResults.filter(turno => {
			if (selectedSpecialtyTab != null && turno.idEspecialidad !== selectedSpecialtyTab) {
				return false
			}

			if (shouldApplyClinicFilter && !fixedClinicTokens.has(String(turno.clinica ?? ''))) {
				return false
			}

			return true
		})
	}, [isMultTurns, searchResults, fixedClinicCodes, fixedClinics, selectedSpecialtyTab])

	const getMultipleDateTimeRangeProps = () => {
		const turnsForRange = baseMultipleTurnsForFilters
		let minHourMinutes = null
		let maxHourMinutes = null

		turnsForRange.forEach(turno => {
			const dateTime = new Date(turno.fechaTurnoDateTime)
			if (Number.isNaN(dateTime.getTime())) return

			const hourMinutes = dateTime.getHours() * 60 + dateTime.getMinutes()

			if (minHourMinutes == null || hourMinutes < minHourMinutes) minHourMinutes = hourMinutes
			if (maxHourMinutes == null || hourMinutes > maxHourMinutes) maxHourMinutes = hourMinutes
		})

		return {
			minDate: new Date(),
			minHourMinutes,
			maxHourMinutes,
			timeIntervals: 5
		}
	}

	const multipleModalFilterFields = [
		{
			type: 'select',
			name: 'professional',
			label: 'Profesional',
			placeholder: 'Seleccionar...',
			options: multipleProfessionalOptions,
			isClearable: true
		},
		{
			type: 'dateTimeRange',
			name: 'dateTimeRange',
			getProps: getMultipleDateTimeRangeProps
		}
	]

	return (
		<Container $padding="0 25px">
			<Header>
				<BackPageHeader
					onBack={onBack}
					title={
						<Title size="24px" color={colors.text.titleText}>
							Resultados de tu búsqueda
						</Title>
					}
					gap="24px"
				/>
			</Header>
			<SearchSectionEditor
				isEditingSearch={isEditingSearch}
				setIsEditingSearch={setIsEditingSearch}
				isMobile={isMobile}
				specialtyName={specialtyName}
				selectedFiltersText={selectedFiltersText}
				formData={formData}
				multipleFiltersFormData={multipleFilters}
				onShowFiltersModal={() => setShowFiltersModal(true)}
				onSearchSubmit={handleSearchSubmit}
				onMultipleFilterSubmit={handleMultipleFilterSubmit}
				filterFieldsProps={filterFieldsProps}
				multipleFilterFieldsProps={multipleFilterFieldsProps}
				isMultTurns={isMultTurns}
			/>

			{showFiltersModal && (
				<>
					{isMultTurns ? (
						<ModalFilter
							showModal={showFiltersModal}
							onClose={() => setShowFiltersModal(false)}
							initialFilters={multipleFilters}
							onApplyFilters={values =>
								handleMultipleModalFilterSubmit(values, () => setShowFiltersModal(false))
							}
							filterFields={multipleModalFilterFields}
							showSort={false}
							center={false}
						/>
					) : (
						<Formik
							initialValues={formData}
							enableReinitialize={true}
							validate={validateDateAndTimeRanges}
							onSubmit={(values, { setErrors }) => {
								const validationErrors = validateDateAndTimeRanges(values)
								if (Object.keys(validationErrors).length > 0) {
									setErrors(validationErrors)
									return
								}
								handleSearchSubmit(values, () => setShowFiltersModal(false))
							}}
						>
							{({ values, handleSubmit, setFieldValue, errors }) => (
								<ModalWrapper>
									<ModalOverlay onClick={() => setShowFiltersModal(false)} />
									<ModalContent>
										<ModalHeader>
											<ModalTitle>Filtros</ModalTitle>
											<CloseButton onClick={() => setShowFiltersModal(false)}>x</CloseButton>
										</ModalHeader>
										<ModalFilterGrid>
											<AppointmentFilterFields
												values={values}
												setFieldValue={setFieldValue}
												errors={errors}
												{...filterFieldsProps}
											/>
										</ModalFilterGrid>
										<ModalFooter>
											<Button label={'Buscar'} onClick={handleSubmit} style={{ maxWidth: '100%' }} />
										</ModalFooter>
									</ModalContent>
								</ModalWrapper>
							)}
						</Formik>
					)}
				</>
			)}

			{groupedMedics?.length === 0 && !isMultTurns ? (
				<EmptyState isSearch={true} actionButton={false} />
			) : (
				<ContentWrapper $showAppointmentCard={showAppointmentCard}>
					<Sidebar $hidden={showMap}>
						{isMultTurns && specialtyTabs.length > 0 && (
							<Tabs tabs={specialtyTabs} activeTab={selectedSpecialtyTab} onChange={setSelectedSpecialtyTab} />
						)}
						<Text2
							as="div"
							size="14px"
							color={colors.text.mutedText}
							style={{ marginBottom: '15px' }}
						>
							{displayedMedics.length} resultados
						</Text2>
						{isMultTurns && (
							<MobileFilterBar
								filtersCount={isMultTurns ? multipleFiltersCount : countSearchFilters({ formData: submittedFilters })}
								onOpenFilters={handleOpenFilters}
								showMap={showMap}
								onToggleView={setShowMap}
								isMultTurns={isMultTurns}
								isMobile={isMobile}
							/>
						)}
						<FilterToggle>
							<span>Turnos para los proximos 7 dias</span>
							<Toggle>
								<ToggleInput
									type="checkbox"
									checked={showNext7Days}
									onChange={e => setShowNext7Days(e.target.checked)}
								/>
								<ToggleSlider />
							</Toggle>
						</FilterToggle>

						<ResultsList>
							{displayedMedics.length === 0 ? (
								<EmptyState isSearch={true} actionButton={false} />
							) : (
								displayedMedics.map(medic => (
									<DoctorCard
										key={medic.idTurno}
										doctor={medic}
										clinicName={clinics.find(x => x.value == medic.clinica)?.label}
										selectable
										selected={selectedMedicId === medic.idTurno}
										onSelect={handleSelectCard}
										onBook={handleBookAppointment}
									/>
								))
							)}
						</ResultsList>
					</Sidebar>
					{(showMap || !isMobile) && (
						<MapWrapper>
							{isMobile && (
								<>
									{!isMultTurns ? (
										<MobileViewToggle showMap={showMap} onToggle={setShowMap} overlay={true} />
									) : (
										<MobileFilterBar
											filtersCount={isMultTurns ? multipleFiltersCount : countSearchFilters({ formData: submittedFilters })}
											onOpenFilters={handleOpenFilters}
											showMap={showMap}
											onToggleView={setShowMap}
											isMultTurns={isMultTurns}
											isMobile={isMobile}
										/>
									)}
								</>
							)}

							<MapContainer
								medics={displayedMedics}
								mapCenterSearched={mapCenter}
								radius={2000}
								onMapPositionChange={handleMapPositionChange}
								selectedMedicId={selectedMedicId}
								onSelectMarker={handleSelectMarker}
								zoom={zoom}
								mapCenter={mapCenter}
							/>
						</MapWrapper>
					)}
				</ContentWrapper>
			)}

			{selectedClinicModal && (
				<ClinicAppointmentsModal
					clinic={selectedClinicModal}
					appointments={filteredSearchResults}
					groupedMedics={groupedMedics}
					clinics={clinics}
					onClose={() => setSelectedClinicModal(null)}
					onBook={handleBookAppointment}
				/>
			)}
			{isMultTurns && <AppointmentCart onContinue={handleCartContinue} disabled={false} />}
		</Container>
	)
}

const mapDispatchToProps = {
	showLoader,
	hideLoader,
	setAppointmentsBookingSearchFetched,
	addFlashMessage,
	setAppointmentsBookingForm,
	setAppointmentsBookingSubmittedFilters,
	setAppointmentsBookingSearchResults,
	setAppointmentsBookingSelectedAppointments,
	setAppointmentsBookingSelectedSpecialtyTab
}

function mapStateToPropsBooking({ auth, appointment }) {
	return {
		patient: auth,
		formData: appointment.booking.formData,
		submittedFilters: appointment.booking.submittedFilters,
		clinics: appointment.booking.catalogs.clinics,
		specialties: appointment.booking.catalogs.specialties,
		professionals: appointment.booking.catalogs.professionals,
		professionalsKey: appointment.booking.catalogs.professionalsKey,
		searchFetched: appointment.booking.searchFetched,
		searchResults: appointment.booking.searchResults,
		cartTurns: appointment.booking.cartTurns,
		selectedSpecialtyTabStored: appointment.booking.selectedSpecialtyTab
	}
}

export default connect(mapStateToPropsBooking, mapDispatchToProps)(SearchMap)
